/** All known ids for a media record (for folder assignment lookup) */
export function getMediaIdAliases(media) {
  if (!media) return []
  const ids = new Set()
  ;[media.id_media, media.id, media.media_asset_id, media.mediaAssets?.[0]?.id].forEach((value) => {
    if (value != null && value !== "") ids.add(String(value))
  })
  return [...ids]
}

/** True when id is a real backend media entity (numeric), not mock/demo data */
export function isApiDeletableMediaId(id) {
  if (id == null || id === "") return false
  const num = Number(id)
  return Number.isFinite(num) && num > 0
}

/** Stable id for folder assignment and list keys */
export function getMediaId(media) {
  if (!media) return null
  return (
    media.id_media ??
    media.id ??
    media.media_asset_id ??
    media.mediaAssets?.[0]?.id ??
    null
  )
}

export function hasRenderableMediaUrl(media) {
  if (!media) return false
  const url =
    media.asset_url ||
    media.proxy_url ||
    media.mediaAssets?.[0]?.asset_url ||
    media.url ||
    ""
  return typeof url === "string" && url.trim().length > 0
}

/** Normalize Media Library asset API records */
export function normalizeApiAsset(item, index = 0) {
  if (!item || typeof item !== "object") return null

  const id = item.id ?? item.assetId
  const url = item.url || item.asset_url || ""
  const typeId = Number(item.typeId ?? item.media_type_id) || 1
  const isVideo = typeId === 2
  const folderRef = item.mediaLibraryFolder
  const folderId =
    folderRef?.id ??
    (folderRef != null && typeof folderRef !== "object" ? folderRef : null)

  return normalizeMediaRecord(
    {
      ...item,
      id,
      id_media: id,
      media_asset_id: id,
      typeId,
      media_type_id: typeId,
      asset_filename: item.name || item.asset_filename || item.filename,
      asset_url: url,
      proxy_url: url,
      url,
      filesize: item.size ?? item.filesize ?? 0,
      inserted_datetime: item.insertedDatetime ?? item.inserted_datetime,
      inserted_datetime_asset: item.insertedDatetime ?? item.inserted_datetime,
      updated_datetime_asset: item.insertedDatetime ?? item.updated_datetime_asset,
      updated_datetime: item.insertedDatetime ?? item.updated_datetime,
      systemName: item.systemName,
      mediaLibraryFolder: folderRef,
      _apiFolderId: folderId != null ? String(folderId) : null,
      mediaAssets: url
        ? [
            {
              asset_url: url,
              mimeType: isVideo ? "video/mp4" : item.mimeType || "image/jpeg",
            },
          ]
        : [],
    },
    index,
  )
}

export function normalizeApiAssetList(response) {
  const raw = Array.isArray(response)
    ? response
    : response?.assets || response?.data || response?.results || []
  if (!Array.isArray(raw)) return []
  return raw.map((item, index) => normalizeApiAsset(item, index)).filter(Boolean)
}

/** Normalize upload API shapes into media records the grid can render */
export function normalizeUploadMedias(apiResult) {
  if (!apiResult) return []
  const raw = Array.isArray(apiResult)
    ? apiResult
    : apiResult.medias || apiResult.results || apiResult.data || []
  if (!Array.isArray(raw)) return []

  return raw.map((item, index) => normalizeMediaRecord(item, index)).filter(Boolean)
}

export function normalizeMediaRecord(media, index = 0) {
  if (!media || typeof media !== "object") return null

  const id = getMediaId(media) ?? `upload-${Date.now()}-${index}`
  const assetUrl =
    media.asset_url ||
    media.proxy_url ||
    media.mediaAssets?.[0]?.asset_url ||
    media.url ||
    ""

  return {
    ...media,
    id_media: id,
    media_asset_id: media.media_asset_id ?? id,
    asset_filename: media.asset_filename || media.filename || media.name || `upload_${index + 1}`,
    asset_url: assetUrl,
    proxy_url: media.proxy_url || assetUrl,
    filesize: media.filesize ?? media.size ?? 0,
    updated_datetime_asset: media.updated_datetime_asset || media.updated_datetime || new Date().toISOString(),
    updated_datetime: media.updated_datetime || media.updated_datetime_asset || new Date().toISOString(),
    mediaAssets:
      media.mediaAssets?.length > 0
        ? media.mediaAssets
        : assetUrl
          ? [{ asset_url: assetUrl, mimeType: media.mimeType || media.mime_type }]
          : [],
  }
}

export function mergeMediaLists(...lists) {
  const merged = []
  const seen = new Set()

  lists.forEach((list) => {
    ;(list || []).forEach((media) => {
      const id = getMediaId(media)
      const key = id != null ? String(id) : `idx-${merged.length}`
      if (seen.has(key)) return
      seen.add(key)
      merged.push(normalizeMediaRecord(media) || media)
    })
  })

  return merged
}

export function resolveFolderForMedia(media, assignments) {
  if (!assignments || !media) return null
  for (const id of getMediaIdAliases(media)) {
    const folderId = assignments[id]
    if (folderId != null) return folderId
  }
  const filename = (media.asset_filename || "").toLowerCase()
  if (filename) {
    const folderId = assignments[`file:${filename}`]
    if (folderId != null) return folderId
  }
  return null
}

export function findMediaByAssignmentId(assignmentId, medias) {
  const target = String(assignmentId)
  return (medias || []).find((media) => getMediaIdAliases(media).includes(target)) || null
}

/** Derive storage stats from in-memory media records (no API). */
export function computeLocalMediaStats(medias = []) {
  const image = {
    mediaTypeId: 1,
    totalCount: 0,
    totalFilesize: 0,
    thisMonthCount: 0,
    lastUpdatedDatetime: null,
  }
  const video = {
    mediaTypeId: 2,
    totalCount: 0,
    totalFilesize: 0,
    thisMonthCount: 0,
    lastUpdatedDatetime: null,
  }

  const startOfMonth = new Date()
  startOfMonth.setDate(1)
  startOfMonth.setHours(0, 0, 0, 0)

  const updateLastUpdated = (bucket, raw) => {
    if (!raw) return
    const safe = typeof raw === "string" && !raw.endsWith("Z") ? `${raw}Z` : raw
    const t = new Date(safe).getTime()
    if (Number.isNaN(t)) return
    const prev = bucket.lastUpdatedDatetime
      ? new Date(
          typeof bucket.lastUpdatedDatetime === "string" && !bucket.lastUpdatedDatetime.endsWith("Z")
            ? `${bucket.lastUpdatedDatetime}Z`
            : bucket.lastUpdatedDatetime,
        ).getTime()
      : -Infinity
    if (t >= prev) bucket.lastUpdatedDatetime = raw
  }

  for (const media of medias) {
    const isVideo =
      media.media_type_id === 2 ||
      media.typeId === 2 ||
      media.dam_metadata?.media_type?.primary_type === "video"
    const bucket = isVideo ? video : image
    bucket.totalCount += 1
    bucket.totalFilesize += media.filesize || 0

    const raw = media.updated_datetime_asset || media.updated_datetime
    if (raw) {
      const safe = typeof raw === "string" && !raw.endsWith("Z") ? `${raw}Z` : raw
      const t = new Date(safe)
      if (!Number.isNaN(t.getTime()) && t >= startOfMonth) {
        bucket.thisMonthCount += 1
      }
      updateLastUpdated(bucket, raw)
    }
  }

  return [image, video]
}

export function getMediaDownloadUrl(media) {
  if (!media) return ""
  return (
    media.asset_url ||
    media.proxy_url ||
    media.mediaAssets?.[0]?.asset_url ||
    media.url ||
    ""
  )
}

export function triggerBlobDownloadFromUrl(url, filename = "download") {
  if (!url) return Promise.resolve(false)

  return fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Download failed (${response.status})`)
      return response.blob()
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = blobUrl
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
      return true
    })
    .catch(() => false)
}

export async function triggerLocalMediaDownload(media) {
  const url = getMediaDownloadUrl(media)
  if (!url) return false

  const filename = media.asset_filename || "download"
  return triggerBlobDownloadFromUrl(url, filename)
}
